import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Phone, Shield } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { usePortalStore } from '../../../stores/portalStore';
import { portalApi, portalApiError } from '../../../lib/portalApi';

type Step = 'phone' | 'otp';

interface OtpRequestResponse { session_token: string; message: string }
interface OtpVerifyResponse {
  access_token: string;
  portal: { tenant_id: string; tenant_name: string; tenant_slug: string };
  guardian_id: string;
  guardian_name: string;
}

export function ParentLoginPage() {
  const navigate = useNavigate();
  const setAuth = usePortalStore(s => s.setAuth);

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [error, setError] = useState('');

  const requestOtp = useMutation({
    mutationFn: (ph: string) =>
      portalApi.post<{ data: OtpRequestResponse }>('/portal/parent/auth/otp/request', { phone: ph }).then(r => r.data.data),
    onSuccess: (data) => {
      setSessionToken(data.session_token);
      setStep('otp');
      setError('');
    },
    onError: (err) => setError(portalApiError(err)),
  });

  const verifyOtp = useMutation({
    mutationFn: () =>
      portalApi.post<{ data: OtpVerifyResponse }>('/portal/parent/auth/otp/verify', {
        session_token: sessionToken,
        otp,
      }).then(r => r.data.data),
    onSuccess: (data) => {
      setAuth({
        role: 'parent',
        accessToken: data.access_token,
        userId: data.guardian_id,
        displayName: data.guardian_name,
        tenantSlug: data.portal.tenant_slug,
        tenantName: data.portal.tenant_name,
      });
      navigate('/portal/parent');
    },
    onError: (err) => setError(portalApiError(err)),
  });

  return (
    <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)] flex items-center justify-center mx-auto mb-3">
            <BookOpen size={22} className="text-white" />
          </div>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">Parent Portal</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">Monitor your child's progress with a one-time code</p>
        </div>

        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-6 shadow-sm">
          {step === 'phone' && (
            <form
              onSubmit={e => { e.preventDefault(); setError(''); requestOtp.mutate(phone); }}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[var(--color-text-secondary)]">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]" />
                  <input
                    className="input pl-9"
                    placeholder="+256 7XX XXX XXX"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    type="tel"
                    autoFocus
                    required
                  />
                </div>
                <p className="text-xs text-[var(--color-text-muted)]">
                  Enter the phone number registered by the school for your account.
                </p>
              </div>

              {error && (
                <p className="text-xs text-[var(--color-danger)] bg-[var(--color-danger)]/5 border border-[var(--color-danger)]/20 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <button type="submit" disabled={requestOtp.isPending} className="btn btn-primary w-full">
                {requestOtp.isPending ? 'Sending OTP…' : 'Send One-Time Code'}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form
              onSubmit={e => { e.preventDefault(); setError(''); verifyOtp.mutate(); }}
              className="flex flex-col gap-4"
            >
              <div className="text-center mb-2">
                <div className="w-10 h-10 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center mx-auto mb-2">
                  <Shield size={18} className="text-[var(--color-primary)]" />
                </div>
                <p className="text-sm text-[var(--color-text-primary)] font-medium">Check your phone</p>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  We sent a 6-digit code to <strong>{phone}</strong>
                </p>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[var(--color-text-secondary)]">One-Time Code</label>
                <input
                  className="input text-center text-xl tracking-[0.4em] font-mono"
                  placeholder="000000"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  required
                />
              </div>

              {error && (
                <p className="text-xs text-[var(--color-danger)] bg-[var(--color-danger)]/5 border border-[var(--color-danger)]/20 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <button type="submit" disabled={verifyOtp.isPending || otp.length < 6} className="btn btn-primary w-full">
                {verifyOtp.isPending ? 'Verifying…' : 'Verify & Sign In'}
              </button>

              <button
                type="button"
                onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
                className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-primary)] text-center"
              >
                ← Use a different number
              </button>
            </form>
          )}

          <p className="text-xs text-[var(--color-text-muted)] text-center mt-4">
            Student?{' '}
            <a href="/portal/student/login" className="text-[var(--color-primary)] hover:underline">
              Student Portal →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
